import Script from 'next/script';
import { jwtDecode, type JwtPayload } from 'jwt-decode';
import { G } from '@mobily/ts-belt';
import { useFeatureFlags } from '@stitch-fix/sf-next';
import {
  isRegistrationResponseExisting,
  registerUser,
} from '../../utils/requests/clientAuth/registration';
import type { SocialButtonsProps } from './SocialButtons';
import { loginByAppleToken } from '../../utils/requests/clientAuth/login';
import { isAndroidApp } from '../../utils/registration/android';
import { SocialButton } from './SocialButton';

const INIT_DATA = {
  clientId: process.env.NEXT_PUBLIC_APPLE_ID,
  scope: 'name email',
  usePopup: true,
  redirectURI: process.env.NEXT_PUBLIC_APPLE_REDIRECT_URI,
};

export type AppleUserProfileData = {
  firstName: string;
  lastName: string;
  email: string;
};

type AppleButtonErrorSource = 'apple' | 'client_auth';

export type OnAppleErrorParams = {
  source: AppleButtonErrorSource;
  message?: string;
};

type AppleButtonProps = Exclude<SocialButtonsProps, 'onError'> & {
  onError: (params: OnAppleErrorParams) => void;
};

export const AppleButton = (props: AppleButtonProps) => {
  const { featureFlags } = useFeatureFlags(
    'visitor',
    'visitor.web.login_when_existing_client_signup',
  );

  // Don't show the Apple button in the Android app
  if (isAndroidApp()) return null;

  const shouldLoginWhenExistingClient =
    featureFlags?.['visitor.web.login_when_existing_client_signup'] === true;

  const handleClick = async () => {
    AppleID.auth.init(INIT_DATA);

    AppleID.auth
      .signIn()
      .then(data => {
        const authData = data?.authorization;
        const userIdentity = jwtDecode<JwtPayload>(authData?.id_token);

        // call `onError` if invalid user data or auth data is returned
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const errorLogValue = (originalValue: any) => {
          if (G.isNull(originalValue)) {
            return '(Null)';
          }
          if (G.isUndefined(originalValue)) {
            return '(Undefined)';
          }
          if (!G.isString(originalValue)) {
            return '(Not a String)';
          }

          return '(String)';
        };

        if (
          !(
            G.isString(authData?.id_token) &&
            G.isString(authData?.code) &&
            G.isString(userIdentity?.sub)
          )
        ) {
          return props.onError({
            message: `AppleButton error: authData.id_token=${errorLogValue(
              authData?.id_token,
            )}, authData.code=${errorLogValue(
              authData?.code,
            )}, userIdentity.sub=${errorLogValue(userIdentity?.sub)}`,
            source: 'apple',
          });
        }

        const userId = userIdentity.sub;

        if (props.mode === 'signup') {
          if (!data.user) {
            if (shouldLoginWhenExistingClient) {
              return loginByAppleToken(
                userId,
                authData.code,
                props.isWafEnabled,
              )
                .then(({ data: clientAuthLoginResponse }) => {
                  return props.onLogin(clientAuthLoginResponse);
                })
                .catch(error => {
                  props.onError({
                    source: 'client_auth',
                    message: `/client-auth-api/api/linked_accounts/apple/login error=${error}`,
                  });
                });
            }

            return props.onError({
              message: 'AppleButton error: Missing data.user',
              source: 'apple',
            });
          }

          const appleUserProfileData: AppleUserProfileData = {
            firstName: data.user.name.firstName,
            lastName: data.user.name.lastName,
            email: data.user.email,
          };

          const {
            onRegistration,
            businessLine,
            zipCode,
            registrationSignupType,
          } = props;

          return (
            /**
             * either create or login a user within our system
             */
            registerUser(
              {
                user: {
                  first_name: appleUserProfileData.firstName,
                  last_name: appleUserProfileData.lastName,
                  email: appleUserProfileData.email,
                  business_line: businessLine,
                  shipping_postcode: zipCode,
                  signup_type: registrationSignupType,
                },
                options: {
                  apple_credentials: {
                    apple_client_id: 'com.stitchfix',
                    apple_user_id: userId,
                    auth_code: authData.code,
                  },
                },
              },
              props.isWafEnabled,
            )
              .then(({ data: clientAuthRegistrationResponse }) => {
                if (
                  isRegistrationResponseExisting(clientAuthRegistrationResponse)
                ) {
                  /**
                   * fire the `onLogin` callback if a user already exists
                   */
                  return props.onLogin(clientAuthRegistrationResponse);
                }

                /**
                 * fire the `onRegistration` callback if a new user was registered
                 */
                return onRegistration(clientAuthRegistrationResponse);
              })

              /**
               * handle any errors from the server
               */
              .catch(error =>
                props.onError({
                  source: 'client_auth',
                  message: `/client-auth-api/api/registration error=${error}`,
                }),
              )
          );
        }

        return loginByAppleToken(userId, authData.code, props.isWafEnabled)
          .then(({ data: clientAuthLoginResponse }) => {
            return props.onLogin(clientAuthLoginResponse);
          })
          .catch(error => {
            props.onError({
              source: 'client_auth',
              message: `/client-auth-api/api/linked_accounts/apple/login error=${error}`,
            });
          });
      })
      .catch(error => {
        if (error.error === 'popup_closed_by_user') {
          return;
        }
        props.onError({
          source: 'apple',
          message: `AppleButton error: AppleID.auth.signIn error=${error.error}`,
        });
      });
  };

  return (
    <>
      <SocialButton
        variant="apple"
        onClick={handleClick}
        compact={props.inline}
        mode={props.mode}
      />

      <Script
        async
        defer
        src="https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js"
      />
    </>
  );
};
